import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Sparkles, Upload, Wand2, Telescope } from "lucide-react";
import { adminApi, adminUpdateGin, adminUpdateDna, adminUploadImage,
         adminGetScript, adminGenerateScript, adminEditScript } from "../../lib/adminApi";
import { DNA_AXES } from "../../lib/constants";
import BottleSilhouette from "../../components/BottleSilhouette";

export default function AdminGinEditor() {
  const { id } = useParams();
  const [gin, setGin] = useState(null);
  const [form, setForm] = useState({});
  const [dna, setDna] = useState({});
  const [script, setScript] = useState({});
  const [aiBusy, setAiBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [imgBusy, setImgBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/gins").then((r) => {
      const g = r.data.gins.find((x) => x.id === id);
      setGin(g);
      if (g) {
        setForm({ name: g.name, country: g.country, region: g.region || "",
                  abv: g.abv, price: g.price, hook: g.hook || "",
                  choose_if: g.choose_if || "", avoid_if: g.avoid_if || "",
                  beno_note: g.beno_note || "", nose: g.nose || "",
                  palate: g.palate || "", finish: g.finish || "",
                  availability_status: g.availability_status || "AVAILABLE" });
        setDna({ ...g.dna });
      }
    });
    adminGetScript(id).then(setScript);
  }, [id]);

  const save = async () => {
    await adminUpdateGin(id, form);
    setSavedAt(new Date().toLocaleTimeString());
  };
  const saveDna = async () => {
    await adminUpdateDna(id, { dna, dna_source: "admin_override", dna_confidence: "high" });
    setSavedAt(new Date().toLocaleTimeString());
  };
  const generateAI = async () => {
    setAiBusy(true);
    try { const s = await adminGenerateScript(id); setScript(s); }
    finally { setAiBusy(false); }
  };
  const saveScript = async () => {
    await adminEditScript(id, script);
    setSavedAt(new Date().toLocaleTimeString());
  };
  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const r = await adminUploadImage(id, file, "", "internal");
      setGin((g) => ({ ...g, image_url: r.image_url, image_state: "approved" }));
    } finally { setImgBusy(false); }
  };

  if (!gin) return <div className="p-10 text-white/40">Loading...</div>;

  return (
    <div className="px-5 md:px-10 py-8 max-w-6xl" data-testid={`admin-gin-editor-${gin.id}`}>
      <Link to="/admin/menu" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59]"><ArrowLeft size={12} /> Menu</Link>
      <header className="mt-6 mb-8 flex items-start gap-6">
        <BottleSilhouette category={gin.category} size={70} />
        <div className="flex-1">
          <p className="overline text-[#C17F59] mb-1">{gin.country} · {gin.styles?.[0]}</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">{form.name}</h1>
          {savedAt && <p className="text-[10px] uppercase tracking-[0.25em] text-green-400 mt-2" data-testid="admin-save-toast">Saved · {savedAt}</p>}
        </div>
        <Link to={`/admin/menu/${gin.id}/enrich`}
              className="hidden md:inline-flex items-center gap-2 bg-[#101113] border border-[#C17F59]/40 text-[#C17F59] hover:bg-[#C17F59] hover:text-[#0E0F0C] px-5 py-3 text-xs uppercase tracking-[0.25em] transition-colors shrink-0"
              data-testid="open-enrichment">
          <Telescope size={14} /> Open Enrichment Scout
        </Link>
      </header>
      <Link to={`/admin/menu/${gin.id}/enrich`}
            className="md:hidden mb-6 inline-flex items-center gap-2 bg-[#101113] border border-[#C17F59]/40 text-[#C17F59] px-5 py-3 text-xs uppercase tracking-[0.25em]"
            data-testid="open-enrichment-mobile">
        <Telescope size={14} /> Open Enrichment Scout
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Core fields */}
        <section className="bg-[#15171A] border border-white/8 p-5">
          <p className="overline text-[#C17F59] mb-3">Core</p>
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="field-name" />
          <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="field-country" />
          <Field label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="field-region" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="ABV %" type="number" value={form.abv} onChange={(v) => setForm({ ...form, abv: parseFloat(v) || 0 })} testid="field-abv" />
            <Field label="Price ₹/30ml" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: parseInt(v) || 0 })} testid="field-price" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Availability" value={form.availability_status} options={["AVAILABLE", "NOT_AVAILABLE", "HIDDEN"]} onChange={(v) => setForm({ ...form, availability_status: v })} testid="field-availability" />
            <div />
          </div>
          <button onClick={save} className="mt-4 inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74]" data-testid="save-core">
            <Save size={12} /> Save core
          </button>
        </section>

        {/* Editorial copy */}
        <section className="bg-[#15171A] border border-white/8 p-5">
          <p className="overline text-[#C17F59] mb-3">Editorial Copy</p>
          <Textarea label="Hook (1-line emotional)" value={form.hook} onChange={(v) => setForm({ ...form, hook: v })} testid="field-hook" />
          <Textarea label="Choose this if" value={form.choose_if} onChange={(v) => setForm({ ...form, choose_if: v })} testid="field-choose" />
          <Textarea label="Avoid this if" value={form.avoid_if} onChange={(v) => setForm({ ...form, avoid_if: v })} testid="field-avoid" />
          <Textarea label="BENO note" value={form.beno_note} onChange={(v) => setForm({ ...form, beno_note: v })} testid="field-beno-note" />
          <div className="grid grid-cols-3 gap-2">
            <Textarea label="Nose" value={form.nose} onChange={(v) => setForm({ ...form, nose: v })} compact testid="field-nose" />
            <Textarea label="Palate" value={form.palate} onChange={(v) => setForm({ ...form, palate: v })} compact testid="field-palate" />
            <Textarea label="Finish" value={form.finish} onChange={(v) => setForm({ ...form, finish: v })} compact testid="field-finish" />
          </div>
          <button onClick={save} className="mt-2 inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74]" data-testid="save-copy">
            <Save size={12} /> Save copy
          </button>
        </section>

        {/* DNA editor */}
        <section className="bg-[#15171A] border border-white/8 p-5" data-testid="admin-dna-editor">
          <p className="overline text-[#C17F59] mb-3">BENO Gin DNA · admin override</p>
          <div className="space-y-2.5">
            {DNA_AXES.map((a) => (
              <div key={a.key} className="flex items-center gap-3">
                <span className="overline text-white/60 w-20 shrink-0">{a.label}</span>
                <input type="range" min={0} max={5} step={1} value={dna[a.key] || 0}
                       onChange={(e) => setDna({ ...dna, [a.key]: parseInt(e.target.value) })}
                       className="flex-1 accent-[#C17F59]"
                       data-testid={`dna-${a.key}`} />
                <span className="font-display text-2xl text-[#C17F59] w-6 text-right tabular-nums">{dna[a.key] || 0}</span>
              </div>
            ))}
          </div>
          <button onClick={saveDna} className="mt-4 inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74]" data-testid="save-dna">
            <Save size={12} /> Save DNA
          </button>
        </section>

        {/* Image upload */}
        <section className="bg-[#15171A] border border-white/8 p-5" data-testid="admin-image-block">
          <p className="overline text-[#C17F59] mb-3">Bottle Image</p>
          <div className="flex items-start gap-4 mb-4">
            <div className="w-24 h-32 border border-white/10 flex items-center justify-center bg-[#101113] shrink-0">
              {gin.image_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${gin.image_url}`} alt={gin.name} className="max-w-full max-h-full object-contain" />
              ) : (
                <BottleSilhouette category={gin.category} size={50} />
              )}
            </div>
            <div className="text-sm text-white/60 space-y-1">
              <p><span className="overline text-white/40">State</span> {gin.image_state || "placeholder"}</p>
              <p><span className="overline text-white/40">License</span> {gin.image_license_status || "—"}</p>
              <p className="text-xs text-white/40">Approved bottle assets only. Max 5MB.</p>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74]" data-testid="upload-image">
            <Upload size={12} /> {imgBusy ? "Uploading..." : "Upload bottle image"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadImg} disabled={imgBusy} />
          </label>
        </section>

        {/* Staff Script */}
        <section className="bg-[#15171A] border border-white/8 p-5 lg:col-span-2" data-testid="admin-script-block">
          <div className="flex items-center justify-between mb-3">
            <p className="overline text-[#C17F59]">Staff Selling Script</p>
            <button onClick={generateAI} disabled={aiBusy}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#C17F59] hover:text-[#D89A74] disabled:opacity-50"
                    data-testid="generate-script">
              <Wand2 size={12} /> {aiBusy ? "Drafting..." : "AI draft"}
              <Sparkles size={12} />
            </button>
          </div>
          <p className="text-xs text-white/40 mb-4">AI drafts. Bartender approves. Editorial only — no operational truth.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Textarea label="10-second pitch" value={script.pitch_10s || ""} onChange={(v) => setScript({ ...script, pitch_10s: v })} testid="script-pitch" />
            <Textarea label="Best guest" value={script.best_guest || ""} onChange={(v) => setScript({ ...script, best_guest: v })} testid="script-guest" />
            <Textarea label="Upsell line" value={script.upsell_line || ""} onChange={(v) => setScript({ ...script, upsell_line: v })} testid="script-upsell" />
            <Textarea label="Avoid if" value={script.avoid_if || ""} onChange={(v) => setScript({ ...script, avoid_if: v })} testid="script-avoid" />
            <Textarea label="Best serve" value={script.best_serve || ""} onChange={(v) => setScript({ ...script, best_serve: v })} testid="script-serve" />
            <Textarea label="Flight pairing" value={script.flight_pairing || ""} onChange={(v) => setScript({ ...script, flight_pairing: v })} testid="script-pairing" />
          </div>
          <button onClick={saveScript} className="mt-4 inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74]" data-testid="save-script">
            <Save size={12} /> Save script
          </button>
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block mb-3">
      <span className="overline text-white/50 mb-1 block">{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none transition-colors"
             data-testid={testid} />
    </label>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block mb-3">
      <span className="overline text-white/50 mb-1 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
              data-testid={testid}>
        {options.map((o) => <option key={o} className="bg-[#15171A]">{o}</option>)}
      </select>
    </label>
  );
}
function Textarea({ label, value, onChange, compact, testid }) {
  return (
    <label className="block mb-3">
      <span className="overline text-white/50 mb-1 block">{label}</span>
      <textarea rows={compact ? 2 : 3} value={value || ""} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border border-white/10 focus:border-[#C17F59] p-2 text-sm focus:outline-none transition-colors resize-y"
                data-testid={testid} />
    </label>
  );
}
